import React, { useEffect, useState } from 'react'

import { Box, Button, Flex, Header, IconButton, Text } from '@driftt/dds'
import { faXmark } from '@driftt/dds.icons/light/faXmark'
import { access } from '@driftt/traffic-control'

import useEducationState from 'hooks/useEducationState'
import { segmentTrack, TrackType } from 'utils/tracking'

interface FilterButtonTooltipProps {
  setShowFiltersMenu: (showFiltersMenu: boolean) => void
}

export const FilterButtonTooltip = ({ setShowFiltersMenu }: FilterButtonTooltipProps) => {
  const { isCheckListOpen, hasFinishedOnboarding, setFinishedOnboarding } = useEducationState()

  const hasOrgSettingsEnabled = access.hasFeatures([
    { featureName: 'filter_admin_settings', accessLevel: 'WRITE' }
  ])

  const [showModal, setShowModal] = useState(hasOrgSettingsEnabled && !isCheckListOpen && !hasFinishedOnboarding)

  useEffect(() => {
    if (showModal) {
      segmentTrack(TrackType.FILTER_BUTTON_TOOLTIP_VIEWED)
    }
  }, [showModal])

  const handleClose = () => {
    setShowModal(false)
    setFinishedOnboarding()
  }

  const handleLearnMore = () => {
    segmentTrack(TrackType.FILTER_BUTTON_TOOLTIP_LEARN_MORE_CLICKED)
    window.open('https://help.salesloft.com/s/article/Drift-Prospector', '_blank', 'noopener,noreferrer')
    handleClose()
  }

  const handleStartFiltering = () => {
    segmentTrack(TrackType.FILTER_BUTTON_TOOLTIP_START_FILTERING_CLICKED)
    setShowFiltersMenu(true)
    handleClose()
  }

  if (!showModal) {
    return null
  }

  return (
    <Box
      css={{
        width: '336px',
        minHeight: '184px',
        backgroundColor: '$indigo10',
        borderRadius: '$1',
        boxShadow: '$1',
        position: 'absolute',
        padding: '$4',
        top: 60,
        right: 0,
        zIndex: '$modal',
        '&:after': {
          content: '" "',
          height: '2rem',
          width: '2rem',
          backgroundColor: '$indigo10',
          position: 'absolute',
          right: '1rem',
          top: 0,
          borderLeft: '1px solid $gray2',
          borderTop: '1px solid $gray2',
          transform: 'translate(-50%, -50%) rotate(45deg)',
          clipPath: 'inset(-15px 0px 0px 0px)',
          shadow: '$1'
        }
      }}>
      <IconButton
        variant="tertiary"
        title=""
        icon={faXmark}
        css={{
          position: 'absolute',
          top: 5,
          right: 4,
          zIndex: '$modal',
          '&:hover': {
            backgroundColor: '$indigo10'
          }
        }}
        onClick={handleClose}
        iconVariant="light"
      />
      <Header variant="h3" css={{ color: '$white', marginBottom: '$2' }}>
        Help reps focus on the accounts they care about
      </Header>
      <Text variant="body" css={{ marginBottom: '$2', color: '$white' }}>
        Use filters to define account segments. Filters can be saved and assigned to reps.
      </Text>
      <Flex gap={0}>
        <Button
          onClick={handleLearnMore}
          css={{
            width: '100%',
            borderColor: '$white',
            backgroundColor: '$indigo10',
            marginRight: '$2',
            '&:hover': {
              backgroundColor: '$indigo10',
              borderColor: '$white'
            }
          }}>
          Learn more
        </Button>
        <Button
          onClick={handleStartFiltering}
          css={{
            width: '100%',
            backgroundColor: '$white',
            color: '$black',
            '&:hover': {
              backgroundColor: '$white',
              color: '$black'
            }
          }}>
          Start filtering
        </Button>
      </Flex>
    </Box>
  )
}
